import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Download, Loader2, Package, PackageCheck, Star, Truck, Wallet } from "lucide-react";
import { downloadInvoice, formatApiError, inr, trackOrder } from "@/lib/api";
import { ReviewFormDialog } from "@/components/ReviewFormDialog";
import { toast } from "@/components/ui/sonner";

const STEPS = [
  { key: "placed", label: "Order placed", icon: Package },
  { key: "confirmed", label: "Confirmed", icon: CheckCircle2 },
  { key: "shipped", label: "Shipped", icon: Truck },
  { key: "delivered", label: "Delivered", icon: PackageCheck },
];

const statusIndex = (status) => {
  if (status === "payment_pending") return 0;
  if (status === "cancelled") return -1;
  const map = { placed: 0, paid: 1, confirmed: 1, shipped: 2, delivered: 3 };
  return map[status] ?? 0;
};

const inputCls =
  "mt-1.5 w-full rounded-md border border-input bg-white/60 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition";

export default function TrackPage() {
  const [orderNo, setOrderNo] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [reviewFor, setReviewFor] = useState(null);

  const invoice = async () => {
    setInvoiceBusy(true);
    try {
      await downloadInvoice(result.order_no, phone);
    } catch {
      toast.error("Could not download invoice — please try again");
    } finally {
      setInvoiceBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setResult(null);
    try {
      setResult(await trackOrder(orderNo, phone));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const active = result ? statusIndex(result.status) : -1;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/50 bg-[#F5F4F0]/85 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="track-logo" className="flex items-center">
            <img src="/classina-logo.png" alt="Classina Products" className="h-8 w-auto" />
          </Link>
          <Link to="/" data-testid="track-back-link" className="text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors">
            ← Back to store
          </Link>
        </div>
      </header>

      <div className="max-w-xl mx-auto px-6 py-20 md:py-28">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-primary mb-4">Order tracking</p>
          <h1 className="font-serif text-4xl md:text-5xl font-semibold tracking-tighter leading-tight">
            Where's my <span className="italic text-primary">order?</span>
          </h1>
          <p className="mt-4 text-muted-foreground text-sm leading-relaxed">
            Enter the order number from your confirmation (starts with CLS-) and the phone number you ordered with.
          </p>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mt-10 bg-white/70 backdrop-blur-sm border border-border/60 rounded-2xl p-7 md:p-8 shadow-sm"
          data-testid="track-form"
        >
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Order number</span>
            <input
              data-testid="track-order-input"
              required
              value={orderNo}
              onChange={(e) => setOrderNo(e.target.value)}
              placeholder="CLS-AC95FBEC"
              className={inputCls}
            />
          </label>
          <label className="block mt-4">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Phone number</span>
            <input
              data-testid="track-phone-input"
              required
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 98765 43210"
              className={inputCls}
            />
          </label>
          {error && <p data-testid="track-error" className="text-sm text-destructive mt-3">{error}</p>}
          <button
            data-testid="track-submit-button"
            type="submit"
            disabled={loading}
            className="w-full mt-6 rounded-full bg-primary text-primary-foreground py-3.5 text-sm font-bold flex items-center justify-center gap-2 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? "Finding your order…" : "Track order"}
          </button>
        </motion.form>

        {result && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="track-result"
            className="mt-8 bg-white/70 backdrop-blur-sm border border-border/60 rounded-2xl p-7 md:p-8 shadow-sm"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-serif text-2xl font-semibold">{result.order_no}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(result.created_at).toLocaleString("en-IN", { dateStyle: "medium" })} · {result.customer_name} · {result.city}
                </p>
              </div>
              <p className="font-serif text-xl font-semibold whitespace-nowrap">{inr(result.total)}</p>
            </div>

            {result.status === "cancelled" ? (
              <p data-testid="track-status" className="mt-6 text-sm font-bold text-red-700 bg-red-100 rounded-full px-4 py-2 inline-block">
                This order was cancelled — contact us if this looks wrong
              </p>
            ) : (
              <div className="mt-8 flex items-center" data-testid="track-status">
                {STEPS.map((s, i) => (
                  <div key={s.key} className="flex-1 flex flex-col items-center relative">
                    {i > 0 && (
                      <div className={`absolute top-4 right-1/2 w-full h-0.5 -z-0 ${i <= active ? "bg-primary" : "bg-border"}`} />
                    )}
                    <div
                      className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        i <= active ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"
                      }`}
                    >
                      <s.icon className="w-4 h-4" />
                    </div>
                    <p className={`text-[10px] uppercase tracking-wider font-bold mt-2 text-center ${i <= active ? "text-primary" : "text-muted-foreground"}`}>
                      {s.label}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-8 pt-5 border-t border-border/60 space-y-1.5">
              {result.items.map((i, idx) => (
                <div key={idx} className="flex items-center justify-between gap-3">
                  <p className="text-sm text-muted-foreground">
                    {i.name}{i.variant ? ` (${i.variant})` : ""}{i.pack ? ` [${i.pack}]` : ""} × {i.qty}
                  </p>
                  {result.status === "delivered" && (
                    <button
                      data-testid={`rate-item-${idx}`}
                      onClick={() => setReviewFor({ slug: i.slug, name: i.name })}
                      className="text-xs font-bold text-amber-600 flex items-center gap-1 hover:underline shrink-0"
                    >
                      <Star className="w-3.5 h-3.5" /> Rate
                    </button>
                  )}
                </div>
              ))}
              <p className="text-xs text-muted-foreground pt-2 flex items-center gap-1.5">
                <Wallet className="w-3.5 h-3.5" />
                {result.status === "paid" ? "Paid online" : result.payment_mode === "online" ? "Online payment pending" : "Pay on confirmation / COD"}
              </p>
              <div className="pt-3 flex flex-wrap gap-3">
                <button
                  data-testid="download-invoice-button"
                  onClick={invoice}
                  disabled={invoiceBusy}
                  className="rounded-full border border-border px-5 py-2.5 text-xs font-bold flex items-center gap-1.5 hover:bg-secondary transition-colors disabled:opacity-60"
                >
                  {invoiceBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  Download invoice (PDF)
                </button>
                {result.status === "delivered" && (
                  <p className="text-xs text-muted-foreground self-center">Delivered — tap ★ Rate to review your products</p>
                )}
              </div>
            </div>
          </motion.div>
        )}

        <ReviewFormDialog
          open={!!reviewFor}
          onOpenChange={(o) => !o && setReviewFor(null)}
          product={reviewFor ? { slug: reviewFor.slug, name: reviewFor.name } : null}
          orderNo={result?.order_no}
          phone={phone}
        />
      </div>
    </div>
  );
}
